import { FlaskConical, Power } from 'lucide-react'
import * as React from 'react'

import { Button } from '@/components/ui/button'
import { useConfirm } from '@/components/ui/confirm-dialog'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Switch } from '@/components/ui/switch'
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip'
import { clearMockRequest, disarmMock, isMockActive, MOCK_LABEL, requestMock } from '@/data/mock/mode'
import { useSession } from '@/lib/session'
import { isViewingAs } from '@/lib/view-as'

/**
 * Botão do modo "dados fictícios" (SIS-465) — só para o dev admin. Ligar pede
 * confirmação e recarrega a aba; a sessão confere o papel no banco antes de
 * armar. Desligar limpa e recarrega com os dados reais.
 */

export const MOCK_SWITCH_LABEL = 'Dados fictícios nesta sessão'
const VIEW_AS_BLOCK = 'Saia do "ver como" para usar os dados fictícios.'

function useMockToggle() {
  const { isAdmin, me } = useSession()
  const { confirm, confirmDialog } = useConfirm()
  const active = isMockActive()
  const blocked = isViewingAs() ? VIEW_AS_BLOCK : null

  const setOn = React.useCallback(
    async (on: boolean) => {
      if (on) {
        const yes = await confirm({
          title: 'Ligar dados fictícios nesta aba?',
          description:
            'A aba passa a mostrar um cenário inventado (pessoas, tarefas, ATAs, integrações) para testar as telas. Nada é gravado no banco nem nos outros sistemas, e as outras abas e pessoas não são afetadas. Desligar, sair ou fechar a aba apaga tudo.',
          confirmLabel: 'Ligar',
        })
        if (!yes || !requestMock(me)) return
      } else {
        clearMockRequest()
        disarmMock()
      }
      window.location.reload()
    },
    [confirm, me],
  )

  return { visible: isAdmin, active, blocked, setOn, confirmDialog }
}

/** Switch com rótulo (Configurações → Desenvolvimento e o menu do usuário). */
export function MockModeSwitch({ id = 'dados-ficticios' }: { id?: string }) {
  const { visible, active, blocked, setOn, confirmDialog } = useMockToggle()
  if (!visible) return null
  const control = (
    <Switch id={id} checked={active} disabled={!!blocked} onCheckedChange={(on) => void setOn(on)} aria-describedby={`${id}-hint`} />
  )
  return (
    <div className="flex items-start gap-3">
      {blocked ? (
        <Tooltip>
          <TooltipTrigger asChild>
            <span tabIndex={0}>{control}</span>
          </TooltipTrigger>
          <TooltipContent>{blocked}</TooltipContent>
        </Tooltip>
      ) : (
        control
      )}
      <div className="min-w-0 space-y-0.5">
        <Label htmlFor={id} className="text-[13px] font-medium">
          {MOCK_SWITCH_LABEL}
        </Label>
        <p id={`${id}-hint`} className="text-xs text-muted-foreground">
          {blocked ?? (active ? 'Ligado: nada do que você fizer é salvo.' : 'Só nesta aba, sem gravar nada.')}
        </p>
      </div>
      {confirmDialog}
    </div>
  )
}

/** Ícone no rodapé da sidebar (perto de "Dev admin") que abre o switch. */
export function MockModeMenuButton() {
  const { isAdmin } = useSession()
  if (!isAdmin) return null
  const active = isMockActive()
  return (
    <Popover>
      <Tooltip>
        <TooltipTrigger asChild>
          <PopoverTrigger asChild>
            <Button variant={active ? 'secondary' : 'ghost'} size="icon-sm" aria-label={`${MOCK_LABEL}${active ? ' (ligado)' : ''}`}>
              <FlaskConical className={active ? 'text-warning' : undefined} />
            </Button>
          </PopoverTrigger>
        </TooltipTrigger>
        <TooltipContent side="right">{MOCK_LABEL} (dev admin)</TooltipContent>
      </Tooltip>
      <PopoverContent side="right" align="end" className="w-80">
        <div className="mb-3 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">Desenvolvimento</div>
        <MockModeSwitch id="dados-ficticios-menu" />
      </PopoverContent>
    </Popover>
  )
}

/** Faixa fixa no topo enquanto o modo vale: não fecha, some só desligando. */
export function MockModeBanner() {
  const { setOn, active, confirmDialog } = useMockToggle()
  if (!active) return null
  return (
    <div
      role="status"
      aria-live="polite"
      className="z-40 flex min-h-10 shrink-0 flex-wrap items-center gap-x-3 gap-y-1 bg-warning px-3 py-1.5 text-[13px] text-warning-foreground sm:px-4"
    >
      <FlaskConical className="h-4 w-4 shrink-0" aria-hidden />
      <span className="min-w-0 flex-1">
        <strong className="font-semibold">Dados fictícios</strong> — nada é salvo. Recarregar a página recomeça o cenário.
      </span>
      <Button
        size="sm"
        variant="outline"
        className="h-7 border-warning-foreground/40 bg-transparent text-warning-foreground hover:bg-warning-foreground/10 hover:text-warning-foreground"
        onClick={() => void setOn(false)}
      >
        <Power /> Desativar
      </Button>
      {confirmDialog}
    </div>
  )
}
